import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Building2, Users, Package, Settings, Plug, RefreshCw,
  CheckCircle, XCircle, Search, Shield, Zap, MessageCircle,
  Home, TrendingUp, BarChart, Save, Eye, EyeOff, LayoutDashboard,
  Mic, Building, Brain, GraduationCap
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Module icons mapping - Aligned with April 2026 Menu Redesign (12 Categories)
const moduleIcons = {
  DASHBOARD: LayoutDashboard,
  SALES: Users,
  PROPERTIES: Building,
  COMMUNICATION: MessageCircle,
  MARKETING: TrendingUp,
  AUTOMATION: Zap,
  AI_HUB: Brain,
  MEETING_INTELLIGENCE: Mic,
  ACADEMY: GraduationCap,
  REPORTING: BarChart,
  SETTINGS: Settings,
  ADMINISTRATION: Shield
};

// Module colors — light-theme variant. Icon tile only; keeps the same
// hue vocabulary as before so users still recognize modules by color.
const moduleColors = {
  DASHBOARD: 'bg-blue-100 text-blue-700',
  SALES: 'bg-cyan-100 text-cyan-700',
  PROPERTIES: 'bg-green-100 text-green-700',
  COMMUNICATION: 'bg-amber-100 text-amber-700',
  MARKETING: 'bg-purple-100 text-purple-700',
  AUTOMATION: 'bg-orange-100 text-orange-700',
  AI_HUB: 'bg-violet-100 text-violet-700',
  MEETING_INTELLIGENCE: 'bg-pink-100 text-pink-700',
  ACADEMY: 'bg-emerald-100 text-emerald-700',
  REPORTING: 'bg-indigo-100 text-indigo-700',
  SETTINGS: 'bg-slate-100 text-slate-700',
  ADMINISTRATION: 'bg-red-100 text-red-700',
};

// Core modules that cannot be disabled
const CORE_MODULES = ['DASHBOARD', 'SALES', 'SETTINGS', 'ADMINISTRATION'];

// Module Card Component
const ModuleCard = ({ module, onToggle, loading }) => {
  const Icon = moduleIcons[module.module_name] || Package;
  const colorClass = moduleColors[module.module_name] || 'bg-slate-500/20 text-slate-400';
  const isCore = CORE_MODULES.includes(module.module_name);
  
  return (
    <Card className={`transition-all shadow-sm hover:shadow-md ${module.is_enabled ? 'border-green-200 bg-green-50/30' : 'border-slate-200 bg-white'}`}>
      <CardContent className="p-5">
        {/* min-h keeps every row the same height regardless of whether
            the description is one or two lines — before this the cards
            danced by ~8px depending on text length. */}
        <div className="flex items-center justify-between gap-4 min-h-[52px]">
          {/* Left: icon tile + text. min-w-0 lets long descriptions
              truncate instead of pushing the right side off-card. */}
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${colorClass}`}>
              <Icon className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-medium text-slate-900 truncate">
                  {module.display_name || module.module_name}
                </h3>
                {isCore && (
                  <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200 shrink-0">
                    Core
                  </Badge>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-0.5 truncate">
                {module.description || 'Module features'}
              </p>
            </div>
          </div>
          {/* Right: status badge + switch, pinned so they always align
              vertically down the column. shrink-0 stops them collapsing
              on narrow viewports. */}
          <div className="flex items-center gap-3 shrink-0">
            <Badge
              variant="outline"
              className={module.is_enabled
                ? 'bg-green-50 text-green-700 border-green-200'
                : 'bg-slate-50 text-slate-500 border-slate-200'}
            >
              {module.is_enabled ? 'Enabled' : 'Disabled'}
            </Badge>
            <Switch
              checked={module.is_enabled}
              onCheckedChange={() => onToggle(module.module_name, !module.is_enabled)}
              disabled={loading || isCore}
              data-testid={`module-toggle-${module.module_name}`}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Organization Modules Panel
const OrganizationModulesPanel = ({ organization, token, onUpdate }) => {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  const fetchModules = useCallback(async () => {
    if (!organization?.id) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/organizations/${organization.id}/modules?token=${token}`
      );
      const data = await response.json();
      
      if (data.success) {
        setModules(data.modules || []);
      }
    } catch (error) {
      console.error('Failed to fetch modules:', error);
    } finally {
      setLoading(false);
    }
  }, [organization?.id, token]);

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  const handleToggleModule = async (moduleName, isEnabled) => {
    setUpdating(true);
    try {
      const response = await fetch(
        `${API_URL}/api/organizations/${organization.id}/modules/${moduleName}?token=${token}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ is_enabled: isEnabled })
        }
      );
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`${moduleName} ${isEnabled ? 'enabled' : 'disabled'}`);
        fetchModules();
        if (onUpdate) onUpdate();
      } else {
        toast.error(data.error || 'Failed to update module');
      }
    } catch (error) {
      toast.error('Failed to update module');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {modules.map((module) => (
        <ModuleCard
          key={module.id || module.module_name}
          module={module}
          onToggle={handleToggleModule}
          loading={updating}
        />
      ))}
      {modules.length === 0 && (
        <div className="text-center py-8 text-slate-500">
          <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>No modules configured for this organization</p>
        </div>
      )}
    </div>
  );
};

// Integration Type Card
const IntegrationTypeCard = ({ integration, configured, onConfigure }) => {
  const getIcon = () => {
    switch (integration.type) {
      case 'whatsapp': return MessageCircle;
      case 'hubspot': return TrendingUp;
      case 'twilio': return Zap;
      case 'elevenlabs': return BarChart;
      case 'sendgrid': return MessageCircle;
      case 'microsoft365': return Settings;
      default: return Plug;
    }
  };
  
  const Icon = getIcon();
  
  return (
    <Card className={`transition-all shadow-sm hover:shadow-md ${configured ? 'border-green-200 bg-green-50/30' : 'border-slate-200 bg-white'}`}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between gap-4 min-h-[52px]">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
              <Icon className="h-5 w-5 text-slate-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-medium text-slate-900 truncate">{integration.name}</h3>
              <p className="text-xs text-slate-500 mt-0.5 truncate">{integration.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {configured ? (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Configured</Badge>
            ) : (
              <Badge variant="outline" className="bg-slate-50 text-slate-500 border-slate-200">Not Configured</Badge>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => onConfigure(integration)}
              data-testid={`configure-${integration.type}`}
            >
              {configured ? 'Edit' : 'Configure'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Organization Integrations Panel
const OrganizationIntegrationsPanel = ({ organization, token }) => {
  const [integrationTypes, setIntegrationTypes] = useState([]);
  const [orgIntegrations, setOrgIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configDialog, setConfigDialog] = useState(null);
  const [credentials, setCredentials] = useState({});
  const [saving, setSaving] = useState(false);
  const [showSecrets, setShowSecrets] = useState({});

  const fetchData = useCallback(async () => {
    if (!organization?.id) return;
    
    try {
      const [typesRes, integrationsRes] = await Promise.all([
        fetch(`${API_URL}/api/organizations/integration-types?token=${token}`),
        fetch(`${API_URL}/api/organizations/${organization.id}/integrations?token=${token}`)
      ]);
      
      const typesData = await typesRes.json();
      const integrationsData = await integrationsRes.json();
      
      if (typesData.success) {
        setIntegrationTypes(typesData.integration_types || []);
      }
      if (integrationsData.success) {
        setOrgIntegrations(integrationsData.integrations || []);
      }
    } catch (error) {
      console.error('Failed to fetch integrations:', error);
    } finally {
      setLoading(false);
    }
  }, [organization?.id, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const isConfigured = (type) => {
    return orgIntegrations.some(i => i.integration_type === type);
  };

  const getExistingCredentials = (type) => {
    const existing = orgIntegrations.find(i => i.integration_type === type);
    return existing?.credentials || {};
  };

  const handleConfigure = (integration) => {
    const existing = getExistingCredentials(integration.type);
    setCredentials(existing);
    setConfigDialog(integration);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(
        `${API_URL}/api/organizations/${organization.id}/integrations/${configDialog.type}?token=${token}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ credentials })
        }
      );
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`${configDialog.name} integration saved`);
        setConfigDialog(null);
        fetchData();
      } else {
        toast.error(data.error || data.detail || 'Failed to save integration');
      }
    } catch (error) {
      toast.error('Failed to save integration');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const response = await fetch(
        `${API_URL}/api/organizations/${organization.id}/integrations/${configDialog.type}?token=${token}`,
        { method: 'DELETE' }
      );
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`${configDialog.name} integration removed`);
        setConfigDialog(null);
        fetchData();
      } else {
        toast.error(data.error || 'Failed to remove integration');
      }
    } catch (error) {
      toast.error('Failed to remove integration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <>
      <div className="space-y-5">
        {integrationTypes.map((integration) => (
          <IntegrationTypeCard
            key={integration.type}
            integration={integration}
            configured={isConfigured(integration.type)}
            onConfigure={handleConfigure}
          />
        ))}
      </div>

      {/* Configure Integration Dialog */}
      <Dialog open={!!configDialog} onOpenChange={() => setConfigDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="integration-config-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plug className="h-5 w-5 text-blue-500" />
              Configure {configDialog?.name}
            </DialogTitle>
            <DialogDescription>
              Enter the credentials for this integration
            </DialogDescription>
          </DialogHeader>

          {configDialog && (
            <div className="space-y-4 mt-4">
              {/* Required Fields */}
              {configDialog.required_fields?.map((field) => (
                <div key={field}>
                  <Label htmlFor={field} className="flex items-center gap-2">
                    {field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    <Badge variant="destructive" className="text-xs">Required</Badge>
                  </Label>
                  <div className="relative">
                    <Input
                      id={field}
                      type={showSecrets[field] ? 'text' : (field.includes('token') || field.includes('secret') || field.includes('key') ? 'password' : 'text')}
                      value={credentials[field] || ''}
                      onChange={(e) => setCredentials({...credentials, [field]: e.target.value})}
                      placeholder={`Enter ${field.replace(/_/g, ' ')}`}
                      data-testid={`input-${field}`}
                    />
                    {(field.includes('token') || field.includes('secret') || field.includes('key')) && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                        onClick={() => setShowSecrets({...showSecrets, [field]: !showSecrets[field]})}
                      >
                        {showSecrets[field] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    )}
                  </div>
                </div>
              ))}

              {/* Optional Fields */}
              {configDialog.optional_fields?.length > 0 && (
                <div className="pt-2 border-t border-slate-200">
                  <p className="text-sm text-slate-500 mb-3">Optional Fields</p>
                  {configDialog.optional_fields.map((field) => (
                    <div key={field} className="mb-3">
                      <Label htmlFor={field}>
                        {field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </Label>
                      <Input
                        id={field}
                        type={showSecrets[field] ? 'text' : (field.includes('token') || field.includes('secret') ? 'password' : 'text')}
                        value={credentials[field] || ''}
                        onChange={(e) => setCredentials({...credentials, [field]: e.target.value})}
                        placeholder={`Enter ${field.replace(/_/g, ' ')}`}
                        data-testid={`input-${field}`}
                      />
                    </div>
                  ))}
                </div>
              )}

              <DialogFooter className="gap-2">
                {isConfigured(configDialog.type) && (
                  <Button 
                    variant="destructive" 
                    onClick={handleDelete}
                    disabled={saving}
                    data-testid="delete-integration-btn"
                  >
                    Remove Integration
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  onClick={() => setConfigDialog(null)}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="save-integration-btn"
                >
                  {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Save
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

// Main Component
const OrganizationModulesManagement = () => {
  const { token, user } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchOrganizations = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/organizations?token=${token}`);
      const data = await response.json();
      
      if (data.success) {
        setOrganizations(data.organizations || []);
        if (data.organizations?.length > 0 && !selectedOrg) {
          setSelectedOrg(data.organizations[0]);
        }
      }
    } catch (error) {
      toast.error('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  }, [token, selectedOrg]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  const filteredOrgs = organizations.filter(org => 
    org.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (user?.role !== 'super_admin') {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <Shield className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Access Denied</h2>
            <p className="text-slate-500">This page is only accessible to Super Admins.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-16" data-testid="org-modules-management">
      {/* Header — hero + inline organization dropdown selector, freeing
          the whole width below for module/integration content. */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center">
            <Package className="h-7 w-7" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              Organization Modules &amp; Integrations
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Manage module access and integration credentials for each organization
            </p>
          </div>
        </div>

        {/* Organization picker — replaces the old left sidebar. Uses the
            shared Select so it matches every other picker in the app. */}
        <div className="w-full md:w-80">
          <Label className="text-xs font-medium text-slate-500 mb-1.5 block">
            Organization
          </Label>
          <Select
            value={selectedOrg?.id || ''}
            onValueChange={(id) => {
              const org = organizations.find((o) => o.id === id);
              if (org) setSelectedOrg(org);
            }}
            disabled={loading || organizations.length === 0}
          >
            <SelectTrigger data-testid="org-select-trigger" className="w-full">
              <SelectValue placeholder={loading ? 'Loading…' : 'Select an organization'} />
            </SelectTrigger>
            <SelectContent>
              {/* Inline search — filters the visible options so long org
                  lists stay manageable without the old sidebar. */}
              <div className="p-2 sticky top-0 bg-popover border-b border-border">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                  <Input
                    placeholder="Search organizations…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-8 h-8 text-sm"
                    onKeyDown={(e) => e.stopPropagation()}
                    data-testid="org-search-input"
                  />
                </div>
              </div>
              {filteredOrgs.length === 0 ? (
                <div className="px-3 py-4 text-sm text-slate-500 text-center">
                  No organizations found
                </div>
              ) : (
                filteredOrgs.map((org) => (
                  <SelectItem
                    key={org.id}
                    value={org.id}
                    data-testid={`org-item-${org.id}`}
                  >
                    <span className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-slate-400" />
                      {org.name}
                    </span>
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Main content — full width now that the org picker moved up. */}
      {selectedOrg ? (
        <Card>
          <CardHeader className="border-b">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-slate-900">
                  <Building2 className="h-5 w-5 text-blue-600" />
                  {selectedOrg.name}
                </CardTitle>
                <CardDescription className="mt-1">
                  Manage modules and integrations for this organization
                </CardDescription>
              </div>
              <Badge
                variant="outline"
                className="font-mono text-xs bg-slate-50 text-slate-600 border-slate-200"
              >
                ID: {selectedOrg.id.slice(0, 8)}…
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-6 pb-6">
            <Tabs defaultValue="modules" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="modules" data-testid="tab-modules">
                  <Package className="h-4 w-4 mr-2" />
                  Modules
                </TabsTrigger>
                <TabsTrigger value="integrations" data-testid="tab-integrations">
                  <Plug className="h-4 w-4 mr-2" />
                  Integrations
                </TabsTrigger>
              </TabsList>

              <TabsContent value="modules">
                <OrganizationModulesPanel
                  organization={selectedOrg}
                  token={token}
                  onUpdate={fetchOrganizations}
                />
              </TabsContent>

              <TabsContent value="integrations">
                <OrganizationIntegrationsPanel
                  organization={selectedOrg}
                  token={token}
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700">Select an Organization</h3>
            <p className="text-sm text-slate-500 mt-1">
              Pick an organization from the dropdown above to manage its modules and integrations
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default OrganizationModulesManagement;
